/**
 * Helpers for Lambda-backed mutations.
 *
 * AppSync returns a Lambda's `{ statusCode, error }` as ordinary data, not as a GraphQL error, so a
 * failed request looks like a success unless the service checks the result. The backend prefixes the
 * message with `Error:` (the request failed) or `Warning:` (the request was blocked by a business
 * rule), e.g. `Warning: Program was not found in Townsend`. Wrapper resolvers that re-raise a legacy
 * handler's error add a second prefix (`Error: Warning: ...`), so the innermost one decides the level.
 */

export type LambdaResultLevel = 'error' | 'warning';

export interface LambdaResultIssue {
  level: LambdaResultLevel;
  message: string;
}

export interface LambdaResultShape {
  statusCode?: number | null;
  error?: string | null;
}

const LEVEL_PREFIX = /^(error|warning):\s*/i;

/**
 * Error thrown for a failed Lambda result. `message` is the backend's text without the level prefix,
 * or a generic fallback when only the status code signals failure.
 */
export class LambdaResultError extends Error {
  readonly level: LambdaResultLevel;

  constructor(issue: LambdaResultIssue) {
    super(issue.message);
    this.name = 'LambdaResultError';
    this.level = issue.level;
  }
}

/**
 * Reads a Lambda result's error message, falling back to its status code when the message is empty
 * @param result - The mutation's response object
 * @returns The level and user-facing message, or null when the request succeeded
 */
export const getLambdaResultIssue = (result: LambdaResultShape | null | undefined): LambdaResultIssue | null => {
  const raw = result?.error?.toString().trim();
  if (!raw) {
    if (typeof result?.statusCode === 'number' && result.statusCode >= 400) {
      return { level: 'error', message: 'The request could not be completed.' };
    }
    return null;
  }

  let message = raw;
  let prefix: string | undefined;
  let match = message.match(LEVEL_PREFIX);
  while (match) {
    prefix = match[1].toLowerCase();
    message = message.slice(match[0].length);
    match = message.match(LEVEL_PREFIX);
  }
  message = message.trim();
  return {
    level: prefix === 'warning' ? 'warning' : 'error',
    message: message || 'The request could not be completed.',
  };
};

/**
 * Throws a {@link LambdaResultError} when a Lambda result carries an error or a failure status
 * @param result - The mutation's response object
 * @throws LambdaResultError if the result contains an error message or an otherwise unexplained failure status
 */
export const throwIfLambdaError = (result: LambdaResultShape | null | undefined): void => {
  const issue = getLambdaResultIssue(result);
  if (issue) {
    throw new LambdaResultError(issue);
  }
};

/**
 * Whether an error came from a Lambda result that was blocked by a business rule rather than
 * failing, so the UI can show it as a warning instead of an error
 */
export const isLambdaWarning = (error: unknown): boolean =>
  error instanceof LambdaResultError && error.level === 'warning';
